import React, { useState } from 'react';
import classNames from 'classnames';
import { useAtom } from 'jotai/index';
import { useNavigate } from 'react-router-dom';
import styles from './css/index.module.scss';
import useInputState from '../../hooks/useInputState';
import Form, { FORM_BUTTON_TYPES, FORM_INPUT_TYPES } from '../../ui-kit/Form';
import BuildingImage from '../../assets/images/Building.png';
import useTranslations from '../../hooks/useTranslations';
import { DICTIONARY } from '../../translations/dictionary';
import sleep from '../../helpers/sleep';
import { closeModalAtom, modalStateAtom, openInfoModalAtom } from '../../atoms/modalAtoms';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../api';
import { windowWidthAtom } from '../../atoms/globalAtoms';
import PersonalDataProcessingModal from '../../components/QuestionsForm/components/PersonalDataProcessingModal';

function Registration() {
  const { t, lang } = useTranslations();
  const navigate = useNavigate();
  const [, setModalState] = useAtom(modalStateAtom);
  const [, closeModal] = useAtom(closeModalAtom);
  const [, openInfoModal] = useAtom(openInfoModalAtom);
  const [windowWidth] = useAtom(windowWidthAtom);

  const [name, setName, nameError, setNameError] = useInputState('');
  const [email, setEmail, emailError, setEmailError] = useInputState('');
  const [company, setCompany, companyError, setCompanyError] = useInputState('');
  const [position, setPosition, positionError, setPositionError] = useInputState('');
  const [checkboxValue, setCheckboxValue, checkboxValueError, setCheckboxValueError] = useInputState(false);

  const [loading, setLoading] = useState(false);

  const onSubmit = (ev) => {
    ev.preventDefault();
    let hasError = false;
    if (!name) {
      setNameError(t(DICTIONARY.nameRequired));
      hasError = true;
    }
    if (!email) {
      setEmailError(t(DICTIONARY.emailRequired));
      hasError = true;
    }
    if (!company) {
      setCompanyError(t(DICTIONARY.companyRequired));
      hasError = true;
    }
    if (!position) {
      setPositionError(t(DICTIONARY.positionRequired));
      hasError = true;
    }
    if (!checkboxValue) {
      setCheckboxValueError(t(DICTIONARY.policyRequired));
      hasError = true;
    }
    if (!hasError) register().then();
  };

  const register = async () => {
    const payload = {
      name,
      email,
      company_name: company,
      company_position: position,
      lang,
      privacy_policy_accepted: checkboxValue,
    };

    const data = await sendRequest({
      request: Api.register,
      payload,
      warnErrorText: 'while registering',
      showErrorInModal: false,
      setLoading,
    });

    if (data?.status === 'ok') {
      openInfoModal({
        text: data.data.message,
        onButtonClick: () => {
          navigate('/login');
          closeModal();
        },
      });
    } else if (data?.data) {
      handleErrors(data.data);
    } else {
      openInfoModal({ text: data?.message || t(DICTIONARY.error) });
    }
  };

  const handleErrors = (data) => {
    if (Array.isArray(data?.name)) setNameError(data.name.join('. '));
    if (Array.isArray(data?.email)) setEmailError(data.email.join('. '));
    if (Array.isArray(data?.company_name)) setCompanyError(data.company_name.join('. '));
    if (Array.isArray(data?.company_position)) setPositionError(data.company_position.join('. '));
  };

  const onCheckBoxClick = () => {
    if (checkboxValue) return setCheckboxValue(false, true);

    setModalState({
      isOpen: true,
      children: <PersonalDataProcessingModal onConfirm={confirmPolicy} />,
      containerStyle: { padding: 0 },
      withCloseButton: true,
      closeTimeoutMS: 0,
    });
  };

  const confirmPolicy = async () => {
    closeModal();
    await sleep(80);
    setCheckboxValue(true, true);
  };

  const inputs = [
    {
      componentType: FORM_INPUT_TYPES.input,
      name: 'name',
      text: t(DICTIONARY.nameInputLabel),
      value: name,
      onChange: setName,
      isRequired: true,
      error: nameError,
    },
    {
      componentType: FORM_INPUT_TYPES.input,
      name: 'email',
      text: t(DICTIONARY.emailInputLabel),
      value: email,
      onChange: setEmail,
      isRequired: true,
      error: emailError,
    },
    {
      componentType: FORM_INPUT_TYPES.input,
      name: 'company',
      text: t(DICTIONARY.company),
      value: company,
      onChange: setCompany,
      isRequired: true,
      error: companyError,
    },
    {
      componentType: FORM_INPUT_TYPES.input,
      name: 'position',
      text: t(DICTIONARY.position),
      value: position,
      onChange: setPosition,
      isRequired: true,
      error: positionError,
    },
    {
      componentType: FORM_INPUT_TYPES.checkbox,
      text: t(DICTIONARY.agreePrivacyPolicy),
      value: checkboxValue,
      error: checkboxValueError,
      onClick: onCheckBoxClick,
    },
  ];

  const buttons = [
    {
      componentType: FORM_BUTTON_TYPES.main,
      text: t(DICTIONARY.register),
      type: 'submit',
      isDisabled: loading,
    },
    {
      componentType: FORM_BUTTON_TYPES.underlined,
      text: t(DICTIONARY.toLogin),
      onClick: () => navigate('/login'),
    },
    {
      componentType: FORM_BUTTON_TYPES.underlined,
      text: t(DICTIONARY.forgotPasswordLink),
      onClick: () => navigate('/password'),
    },
  ];

  return (
    <div className={styles.registration}>
      <div className={classNames('container')}>
        <div className={styles.registrationInner}>
          <Form
            title={t(DICTIONARY.registration)}
            subtitle={t(DICTIONARY.registerFormSubTitle)}
            inputs={inputs}
            buttons={buttons}
            onSubmit={onSubmit}
            source={BuildingImage}
            maxHeight={windowWidth * 0.48}
          />
        </div>
      </div>
    </div>
  );
}

export default Registration;
